/**
 * Deleted Items Service — Recycle Bin
 *
 * When a user deletes a parcel person, invoice, or arenda distribution,
 * the full original data is saved here BEFORE being removed from the main list.
 * Items can be restored from the Settings → Coș de Gunoi tab.
 *
 * Firestore path: deletedItems/{farmId}  →  { items: [...] }
 * Each item keeps all original fields plus:
 *   _deletedId    — unique ID for this deletion event
 *   _deletedAt    — ISO timestamp
 *   _deletedBy    — user display name / email
 *   _originalType — 'parcels' | 'invoice' | 'arenda'
 *   _label        — human-readable description shown in the recycle bin UI
 */

import { db } from '../config/firebase';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';

class DeletedItemsService {

  /**
   * Save one or more items to the recycle bin before deleting them.
   * @param {string} farmId
   * @param {Array|Object} items  — the item(s) being deleted
   * @param {'parcels'|'invoice'|'arenda'} type
   * @param {string} label  — short human-readable description, e.g. "Ion Popescu (3 parcele)"
   * @param {string} deletedBy  — user name/email
   */
  async saveToRecycleBin(farmId, items, type, label, deletedBy = 'utilizator') {
    try {
      const existing = await this._getItems(farmId);

      const entry = {
        _deletedId: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        _deletedAt: new Date().toISOString(),
        _deletedBy: deletedBy,
        _originalType: type,
        _label: label,
        _data: Array.isArray(items) ? items : [items],
      };

      const updated = [entry, ...existing];
      await setDoc(doc(db, 'deletedItems', farmId), {
        items: updated,
        lastUpdated: new Date().toISOString(),
      });

      return entry._deletedId;
    } catch (error) {
      // Non-blocking — if recycle bin fails, the main delete still proceeds
      console.error('⚠️ Recycle bin save failed (non-blocking):', error);
      return null;
    }
  }

  /**
   * Permanently remove one entry from the recycle bin.
   */
  async permanentlyDelete(farmId, deletedId) {
    const existing = await this._getItems(farmId);
    const updated = existing.filter(item => item._deletedId !== deletedId);
    await setDoc(doc(db, 'deletedItems', farmId), {
      items: updated,
      lastUpdated: new Date().toISOString(),
    });
  }

  /**
   * Get all items currently in the recycle bin.
   */
  async getAll(farmId) {
    return this._getItems(farmId);
  }

  /**
   * Subscribe to real-time changes in the recycle bin.
   * Returns an unsubscribe function.
   */
  subscribe(farmId, callback) {
    return onSnapshot(doc(db, 'deletedItems', farmId), (snap) => {
      if (snap.exists()) {
        callback(snap.data().items || []);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('Recycle bin listener error:', error);
      callback([]);
    });
  }

  async _getItems(farmId) {
    try {
      const snap = await getDoc(doc(db, 'deletedItems', farmId));
      if (!snap.exists()) return [];
      return snap.data().items || [];
    } catch {
      return [];
    }
  }
}

const deletedItemsService = new DeletedItemsService();
export default deletedItemsService;
